import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  fetchFoodIngredientDetail,
  fetchFoodIngredients,
  updateFoodIngredient,
  type FoodIngredientDetail,
} from "../foodApi";
import { money, unitMoney, COMMON_UNITS, COST_SOURCE_LABEL } from "../utils/foodFormatters";
import { QueryError } from "../components/QueryError";
import { SimpleTable } from "../components/SimpleTable";

type MenuUsageRow = FoodIngredientDetail["menuItems"][number];

const MENU_USAGE_COLUMNS: {
  key: string;
  title: string;
  render: (r: MenuUsageRow) => ReactNode;
}[] = [
  { key: "name", title: "Menu Item", render: (r) => r.name },
  { key: "quantity", title: "Quantity", render: (r) => `${r.quantity} ${r.unit}` },
  { key: "price", title: "Price", render: (r) => money(r.currentPrice) },
];

export function IngredientDetailView() {
  const queryClient = useQueryClient();
  const params = useParams<{ id: string }>();
  const id = Number(params.id);

  const [draftName, setDraftName] = useState("");
  const [draftCategory, setDraftCategory] = useState("");
  const [draftUnit, setDraftUnit] = useState("");
  const [draftCostOverride, setDraftCostOverride] = useState("");
  const [draftActive, setDraftActive] = useState(true);
  const [draftIsBatch, setDraftIsBatch] = useState(false);
  const [draftYieldQty, setDraftYieldQty] = useState("");
  const [draftYieldUnit, setDraftYieldUnit] = useState("");
  const [batchRows, setBatchRows] = useState<
    Array<{ ingredientId: number; quantity: string; unit: string }>
  >([]);

  const ingredientsQuery = useQuery({
    queryKey: ["food", "ingredients"],
    queryFn: fetchFoodIngredients,
  });
  const q = useQuery({
    queryKey: ["food", "ingredients", id],
    queryFn: () => fetchFoodIngredientDetail(id),
    enabled: Number.isFinite(id),
  });
  const d: FoodIngredientDetail | undefined = q.data;

  useEffect(() => {
    if (!d) return;
    setDraftName(d.name);
    setDraftCategory(d.category);
    setDraftUnit(d.standardUnit);
    setDraftCostOverride(d.costOverride != null ? String(d.costOverride) : "");
    setDraftActive(d.isActive);
    setDraftIsBatch(d.isBatch);
    setDraftYieldQty(d.batchYieldQuantity == null ? "" : String(d.batchYieldQuantity));
    setDraftYieldUnit(d.batchYieldUnit ?? "");
    setBatchRows(
      d.batchComponents.map((row) => ({
        ingredientId: row.ingredientId,
        quantity: String(row.quantity),
        unit: row.unit,
      })),
    );
  }, [d?.id]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateFoodIngredient(id, {
        name: draftName.trim(),
        category: draftCategory.trim(),
        standardUnit: draftUnit.trim(),
        costOverride: draftCostOverride.trim() ? parseFloat(draftCostOverride) : null,
        isActive: draftActive,
        isBatch: draftIsBatch,
        batchYieldQuantity:
          draftIsBatch && draftYieldQty.trim() ? Number(draftYieldQty) : null,
        batchYieldUnit: draftIsBatch ? draftYieldUnit.trim() || null : null,
        batchComponents: draftIsBatch
          ? batchRows
              .map((row) => ({
                ingredientId: row.ingredientId,
                quantity: Number(row.quantity),
                unit: row.unit,
              }))
              .filter(
                (row) => Number.isFinite(row.ingredientId) && row.ingredientId > 0,
              )
          : [],
      }),
    onSuccess: () => {
      // Cost/unit and every menu cost that uses this ingredient may change.
      queryClient.invalidateQueries({ queryKey: ["food"] });
    },
  });

  if (!Number.isFinite(id)) return <QueryError message="Invalid ingredient id." />;
  if (q.isLoading) return <div className="p-6 text-tribal-muted">Loading ingredient...</div>;
  if (q.isError || !d) {
    return <QueryError message={`Failed to load ingredient: ${(q.error as Error).message}`} />;
  }

  // Archived ingredients can't be picked, but a component that already uses
  // one stays visible so the recipe isn't silently changed.
  const selectedIds = new Set(batchRows.map((row) => row.ingredientId));
  const candidateIngredients = (ingredientsQuery.data ?? []).filter(
    (row) => row.id !== d.id && (row.isActive || selectedIds.has(row.id)),
  );

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
        <h2 className="text-lg font-semibold">Ingredient detail</h2>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-6">
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">Name</label>
            <input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className="w-full rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">Category</label>
            <input
              value={draftCategory}
              onChange={(e) => setDraftCategory(e.target.value)}
              className="w-full rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">Unit</label>
            <input
              value={draftUnit}
              onChange={(e) => setDraftUnit(e.target.value)}
              className="w-full rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">
              Cost / {d.standardUnit || "unit"}
            </label>
            <div
              className="rounded-md border border-tribal-border bg-white/[0.03] px-2 py-1.5 text-sm"
              title={d.costPerUnitSource ? COST_SOURCE_LABEL[d.costPerUnitSource] : undefined}
            >
              {d.costPerUnit != null ? unitMoney(d.costPerUnit) : "—"}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">
              Manual cost / {draftUnit || "unit"}
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-tribal-muted">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={draftCostOverride}
                onChange={(e) => setDraftCostOverride(e.target.value)}
                placeholder="—"
                className="w-full rounded-md border border-tribal-border bg-black/20 pl-5 pr-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 self-end pb-1.5 text-sm text-tribal-muted">
            <input
              type="checkbox"
              checked={draftActive}
              onChange={(e) => setDraftActive(e.target.checked)}
            />
            Active
          </label>
        </div>
        <p className="mt-2 text-xs text-tribal-muted">
          {d.costPerUnit != null && d.costPerUnitSource
            ? `Cost / ${d.standardUnit} is calculated ${COST_SOURCE_LABEL[d.costPerUnitSource]}.`
            : `Cost / ${d.standardUnit} can't be calculated yet: no invoice line or batch recipe converts to "${d.standardUnit}".`}{" "}
          Manual cost is only used when there is no invoice or batch cost.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-tribal-muted">
            <input
              type="checkbox"
              checked={draftIsBatch}
              onChange={(e) => setDraftIsBatch(e.target.checked)}
            />
            Batch ingredient
          </label>
          {draftIsBatch && (
            <>
              <input
                value={draftYieldQty}
                onChange={(e) => setDraftYieldQty(e.target.value)}
                placeholder="Yield qty"
                className="w-32 rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
              />
              <input
                value={draftYieldUnit}
                onChange={(e) => setDraftYieldUnit(e.target.value)}
                placeholder="Yield unit"
                className="w-32 rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
              />
            </>
          )}
        </div>
        {draftIsBatch && (
          <div className="mt-4 space-y-2">
            <div className="text-xs uppercase tracking-wide text-tribal-muted">Batch components</div>
            {batchRows.map((row, idx) => (
              <div
                key={`${row.ingredientId}-${idx}`}
                className="grid grid-cols-1 gap-2 sm:grid-cols-4"
              >
                <select
                  value={row.ingredientId}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setBatchRows((prev) =>
                      prev.map((r, i) => (i === idx ? { ...r, ingredientId: value } : r)),
                    );
                  }}
                  className="rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
                >
                  <option value={0}>Select ingredient</option>
                  {candidateIngredients.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                      {!opt.isActive ? " (archived)" : ""}
                    </option>
                  ))}
                </select>
                <input
                  value={row.quantity}
                  onChange={(e) =>
                    setBatchRows((prev) =>
                      prev.map((r, i) => (i === idx ? { ...r, quantity: e.target.value } : r)),
                    )
                  }
                  placeholder="Quantity"
                  className="rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
                />
                <select
                  value={row.unit}
                  onChange={(e) =>
                    setBatchRows((prev) =>
                      prev.map((r, i) => (i === idx ? { ...r, unit: e.target.value } : r)),
                    )
                  }
                  className="rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
                >
                  {COMMON_UNITS.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setBatchRows((prev) => prev.filter((_, i) => i !== idx))}
                  className="rounded bg-white/5 px-2 py-1 text-xs text-tribal-muted hover:text-white"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setBatchRows((prev) => [...prev, { ingredientId: 0, quantity: "", unit: "each" }])
              }
              className="rounded bg-white/5 px-2 py-1 text-xs text-tribal-muted hover:text-white"
            >
              Add component
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="mt-3 rounded-md bg-tribal-red px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
        >
          {saveMutation.isPending ? "Saving..." : "Save changes"}
        </button>
      </div>
      <section className="rounded-lg border border-tribal-border bg-tribal-surface">
        <div className="border-b border-tribal-border px-4 py-3 text-sm font-medium">
          Menu usage
        </div>
        <SimpleTable<MenuUsageRow>
          rows={d.menuItems}
          empty="No menu usage."
          columns={MENU_USAGE_COLUMNS}
        />
      </section>
    </div>
  );
}
